import type { DrawingDocument } from '../model';
import type { EditorSelection } from './editor';
import { moleculeComponents } from './reactionGraph';
import { moleculeEdges } from './documentSelectors';

// #757 -- what the Selection export scope draws (docs/model/drawing-ui-export.md).
export interface ExportSelection {
  // Every atom of every molecule the selection touches: partial molecules are never exported, and
  // the dialog doesn't ask -- the preview shows the whole molecule (owner, October 5).
  readonly atomIds: ReadonlySet<string>;
  // Selected annotations only; an attached arrow does not pull in its target.
  readonly annotationIds: ReadonlySet<string>;
  readonly empty: boolean;
}

export function exportSelection(document: DrawingDocument, selection: EditorSelection): ExportSelection {
  const { nodes, edges } = document.structure.graph;
  const edgeEnds = new Map(edges.map((edge) => [edge.id, [edge.source, edge.target] as const]));
  const touched = new Set([...selection.atomIds, ...selection.bondIds.flatMap((id) => edgeEnds.get(id) ?? [])]);
  const coordinates = document.depiction.coordinateSet.coordinates;
  const components = moleculeComponents(
    nodes.map((node) => node.id),
    moleculeEdges(document.structure),
    (id) => coordinates[id]
  );
  const atomIds = new Set<string>();
  for (const component of components) {
    if (!component.atomIds.some((id) => touched.has(id))) continue;
    for (const id of component.atomIds) atomIds.add(id);
  }
  const existing = new Set(document.annotations.map((annotation) => annotation.id));
  const annotationIds = new Set((selection.annotationIds ?? []).filter((id) => existing.has(id)));
  return { atomIds, annotationIds, empty: atomIds.size === 0 && annotationIds.size === 0 };
}
